import { Typography } from "antd";
import { ControllerBindingRow } from "./ControllerBindingRow";
import { PlayersForm } from "./PlayersForm";
import { useControllers } from "../../Contexts/dashboardHooks";
import type { SubjectEntity } from "../../Processing/types";
import { boundController, controllerAthlete } from "../../Processing/controllerBindings";

const { Text } = Typography;

/**
 * Exactly ONE athlete. A body map renders a single athlete and a player card holds a bare
 * player_id. When the section is dynamically controlled, it follows a subject controller
 * and shows the FIRST athlete in that controller's picker (drills are ignored).
 */
export interface SinglePlayerFormFields {
  /** UUID of the Slicer this athlete section follows, when it is bound. */
  singlePlayerControllerId?: string;
  /** `player-<id>`, or "" when unset. */
  selectedPlayer: string;
}

export const SINGLE_PLAYER_DEFAULTS: SinglePlayerFormFields = { selectedPlayer: "" };

interface SinglePlayerFormProps {
  value: SinglePlayerFormFields;
  onChange: (patch: Partial<SinglePlayerFormFields>) => void;
  getSubjectEntities: (subjectType: string) => SubjectEntity[];
  description?: string;
  /** Show the "Dynamically controlled" row — the host widget's `dynamic.bindableSections`. */
  bindable?: boolean;
  /** Select size. Omit for antd's default (middle). */
  size?: "small" | "middle";
  onPopupOpenChange?: (open: boolean) => void;
}

export function SinglePlayerForm({
  value,
  onChange,
  getSubjectEntities,
  description = "Choose one athlete.",
  bindable = false,
  size,
  onPopupOpenChange,
}: SinglePlayerFormProps) {
  const controllers = useControllers();
  const controller = bindable
    ? boundController(value.singlePlayerControllerId, "single_player", controllers)
    : undefined;
  const isBound = controller != null;
  const shownPlayer = isBound
    ? controllerAthlete(controller.controls, controller.spec.slicer_builder_misc)
    : value.selectedPlayer;

  return (
    <div className="awm-step-body">
      {bindable && (
        <ControllerBindingRow
          kind="single_player"
          controllerId={value.singlePlayerControllerId}
          onChange={(singlePlayerControllerId) => onChange({ singlePlayerControllerId })}
        />
      )}

      <div className={`awm-form-stack${isBound ? " awm-bound-section" : ""}`}>
        {isBound && !shownPlayer && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            No athlete picked on the controller yet — this widget keeps its own until one is.
          </Text>
        )}
        <PlayersForm
          value={{
            selectedSubjects: shownPlayer ? [shownPlayer] : [],
            selectedDrills: [],
            subjectActiveTab: "athletes",
          }}
          onChange={(patch) =>
            onChange({ selectedPlayer: patch.selectedSubjects?.[0] ?? "" })
          }
          getSubjectEntities={getSubjectEntities}
          singleSelect
          hideDrills
          playersOnly
          description={description}
          size={size}
          onPopupOpenChange={onPopupOpenChange}
        />
      </div>
    </div>
  );
}
